import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';

import { Button } from '@common/components';
import { FormError } from '@common/components/form/FormError';
import { FormField } from '@common/components/form/FormField';
import { Label } from '@common/components/form/Label';
import { Textarea } from '@common/components/form/Textarea';
import { Stack } from '@common/components/Stack';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslations } from '@lang';
import { useNewCancelOrder } from '../mutations';

import { mapOrderDetails } from '../helpers/mapOrderDetails.helper';
import { OrderType } from '../schemas';
import { OrderSummary } from './OrderSummary';
import { useLabels } from '@features/Menu/useLabels';

const CancelOrderFormSchema = z.object({
  orderItemIds: z.array(z.number()),
  message: z.string().min(1),
});

export type CancelOrderFormValues = z.infer<typeof CancelOrderFormSchema>;

type CancelOrderFormProps = {
  onOrderRequestSuccess?: () => void;
  siteUuid: string;
  order: OrderType;
};

export function NewCancelOrderForm({
  onOrderRequestSuccess,
  siteUuid,
  order,
  ...rest
}: CancelOrderFormProps) {
  const { t } = useTranslations();
  const labels = useLabels();

  const {
    mutate: cancelOrder,
    isLoading: isCancelLoading,
    isError: isCancelError,
    error: cancelError,
  } = useNewCancelOrder({
    onSuccess: onOrderRequestSuccess,
  });

  const form = useForm<CancelOrderFormValues>({
    resolver: zodResolver(CancelOrderFormSchema),
    defaultValues: { orderItemIds: [] },
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = form;

  const orderDetails = mapOrderDetails(order, {
    diningLabel: labels?.tables || t('basketDrawer.dining'),
  });

  const submitHandler = handleSubmit((data) => {
    cancelOrder({
      reason: data.message,
      orderId: order.id,
      siteUuid,
      orderItemIds: data.orderItemIds,
    });
  });

  return (
    <>
      <FormProvider {...form}>
        <form {...rest}>
          <OrderSummary orderItems={orderDetails} />
          <fieldset disabled={isCancelLoading}>
            <Stack>
              {isCancelError && <FormError error={cancelError} />}

              <FormField>
                <Label htmlFor="message" required>
                  {t('cancelOrderForm.amend.label')}
                </Label>

                <Textarea
                  {...register('message')}
                  data-testid="order-request-text-area"
                  id="message"
                  size="xl"
                  rows={5}
                  placeholder={t('cancelOrderForm.amend.placeholder')}
                  hasError={!!errors.message}
                />
              </FormField>

              <FormField>
                <Button
                  isPrimary
                  onClick={submitHandler}
                  type="button"
                  disabled={!isValid || isCancelLoading}
                >
                  {t('cancelOrderForm.submit.label')}
                </Button>
              </FormField>
            </Stack>
          </fieldset>
        </form>
      </FormProvider>
    </>
  );
}
